import { ReactNode } from "react";
import { Checkbox, Icon, Tooltip, type CheckboxProps } from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { useController, useFormContext, type FieldValues, type Path } from "react-hook-form";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormCheckboxProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
    CheckboxProps,
    "checked" | "onChange" | "label"
> {
    name: Path<TFieldValues> | (string & NonNullable<unknown>);
    label: ReactNode;
    tooltipContent?: ReactNode;
    checked?: boolean;
    onChange?: (val: boolean, field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    /** Optional side-effect fired after toggle, e.g. for analytics or cascading values */
    onChangeSideEffect?: (newValue: boolean) => void;
    /** Override the error shown (e.g. for custom local-state validation instead of/in addition to schema validation). */
    error?: string | boolean;
}

export function FormCheckbox<TFieldValues extends FieldValues = FieldValues>({
    name,
    label,
    tooltipContent,
    checked,
    onChange,
    onChangeSideEffect,
    disabled,
    error,
    ...rest
}: FormCheckboxProps<TFieldValues>) {
    const { control } = useFormContext<TFieldValues>();

    const {
        field,
        fieldState: { error: fieldError },
    } = useController<TFieldValues>({ control, name: name as any });

    const isAbDisabled = useVariantFieldDisabled(name as string);

    const handleChange = (val: boolean) => {
        if (onChange) {
            onChange(val, field);
        } else {
            field.onChange(val);
        }
        if (onChangeSideEffect) onChangeSideEffect(val);
    };

    const isChecked = checked !== undefined ? checked : Boolean(field.value);

    return (
        <Checkbox
            {...rest}
            disabled={disabled || isAbDisabled}
            label={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}
                >
                    {label}
                    {tooltipContent && (
                        <Tooltip content={tooltipContent} width="wide">
                            <Icon source={InfoIcon} tone="subdued" />
                        </Tooltip>
                    )}
                </div>
            }
            checked={isChecked}
            onChange={handleChange}
            error={error !== undefined ? error : !!fieldError}
        />
    );
}
